/**
 * The ONE element in the grid module that a screen reader speaks unprompted.
 *
 *   ┌ always mounted, always empty-capable ──────────────────────────────┐
 *   │ <div role="status" aria-live="polite" aria-atomic class="sr-only"> │
 *   │   <span key={seq}>…the sentence…</span>                            │
 *   └────────────────────────────────────────────────────────────────────┘
 *        ▲                              ▲
 *        │ text=""  → silent            │ seq moves → ONE utterance
 *
 * ALWAYS MOUNTED, even while silent. A live region has to be in the DOM
 * BEFORE its content changes for assistive tech to observe the change;
 * mounting the region and its text in the same commit is the classic reason
 * an announcement never arrives. So an empty sentence renders the region empty
 * rather than rendering nothing.
 *
 * THE SENTENCE HANGS OFF `seq`, NOT OFF ITSELF. What assistive tech reacts to
 * is a mutation inside the region, and two consecutive announcements are
 * routinely the same words ("1 filter applied, 1 result" before and after the
 * user edits that filter's VALUE). Re-rendering the same string mutates
 * nothing, so the child is keyed on the counter: a new key swaps the node, and
 * the swap is the utterance. `seq` unchanged leaves the DOM untouched, which is
 * what keeps an ordinary re-render silent.
 *
 * `sr-only` (Tailwind) clips it to 1px out of flow: nothing on screen moves,
 * nothing takes layout, and `position: absolute` keeps it out of the parent's
 * flex/grid track — the same technique `ReferenceCodeChip.tsx` already ships.
 *
 * `aria-atomic` so the WHOLE sentence is re-read on change; without it a
 * screen reader may read only the changed words and hand back "47" alone.
 */
import type { GridAnnouncement } from './gridAnnouncements';

export type GridLiveRegionProps = {
  /** What to announce. An empty `text` keeps the region present and silent. */
  announcement: GridAnnouncement;
  'data-testid'?: string;
};

export default function GridLiveRegion({ announcement, 'data-testid': dataTestId }: GridLiveRegionProps) {
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" data-testid={dataTestId}>
      <span key={announcement.seq}>{announcement.text}</span>
    </div>
  );
}
